import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Smartphone, X } from "lucide-react";
import { enablePush, pushSupported, syncPush } from "@/lib/push";
import { useTeam } from "@/lib/team";
import { getInstallPrompt } from "@/lib/installPrompt";

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isPhone = () => window.matchMedia?.("(pointer: coarse)").matches && window.innerWidth < 900;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const hidden = (k) => localStorage.getItem(k) === "1";

function Card({ icon: Icon, title, children, onClose, testid }) {
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-[#0053DA]/20 bg-[#0053DA]/5 p-3 text-xs text-slate-700" data-testid={testid}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#0053DA]" />
      <div className="min-w-0 flex-1"><p className="font-semibold text-slate-900">{title}</p>{children}</div>
      <button type="button" aria-label="Dismiss" onClick={onClose} className="shrink-0 rounded p-1 text-slate-500 hover:bg-white" data-testid={`${testid}-dismiss`}><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// Phones only: nudge everyone to add SnapList to the home screen, then turn on free alerts.
export default function AppSetupCard() {
  const { isAssistant } = useTeam();
  const [prompt, setPrompt] = useState(getInstallPrompt);
  const [, bump] = useState(0);
  const close = (k) => { localStorage.setItem(k, "1"); bump((n) => n + 1); };
  useEffect(() => {
    const on = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", on);
    syncPush().catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);
  if (!isPhone()) return null;
  const canPush = pushSupported() && (standalone() || !isIOS());
  // Team members get the animated install popup instead (PcInstallGuide).
  if (!standalone() && !isAssistant && !hidden("snaplist.installHidden")) {
    const install = async () => { prompt.prompt(); if ((await prompt.userChoice).outcome === "accepted") close("snaplist.installHidden"); };
    return (
      <Card icon={Smartphone} title="Add SnapList to your home screen for the full experience" onClose={() => close("snaplist.installHidden")} testid="install-card">
        <p className="mt-0.5">Opens full screen like an app, one tap away, with free alerts and a communication portal for virtual assistants.</p>
        {prompt ? <button type="button" onClick={install} className="mt-2 rounded-full bg-[#0053DA] px-3 py-1 font-semibold text-white" data-testid="install-button">Install SnapList</button>
          : isIOS() ? <p className="mt-1" data-testid="install-ios-steps">In Safari, tap <b>Share</b> (square with an arrow), then <b>Add to Home Screen</b>. Then open SnapList from the new icon.</p>
          : <p className="mt-1" data-testid="install-android-steps">In Chrome, tap the <b>⋮</b> menu, then <b>Install app</b> or <b>Add to Home screen</b>.</p>}
      </Card>
    );
  }
  if (canPush && Notification.permission === "default" && !hidden("snaplist.alertsHidden")) {
    const turnOn = async () => {
      try {
        const r = await enablePush();
        if (r === "granted") toast.success("Alerts are on. A test alert is on its way.");
        else toast.message("Alerts are off. You can turn them on later in your phone's settings.");
      } catch { toast.error("Couldn't turn on alerts"); }
      bump((n) => n + 1);
    };
    return (
      <Card icon={BellRing} title="Turn on free alerts" onClose={() => close("snaplist.alertsHidden")} testid="alerts-card">
        <p className="mt-0.5">Get a pop-up for new photos to list, questions and answers, even when SnapList is closed.</p>
        <button type="button" onClick={turnOn} className="mt-2 rounded-full bg-[#0053DA] px-3 py-1 font-semibold text-white" data-testid="alerts-enable">Turn on alerts</button>
      </Card>
    );
  }
  return null;
}
